/**
 * Button. Implements DESIGN.md §Components > Buttons: full pill, 44px minimum height,
 * 14px/700, 120ms ease-out; primary / quiet / ghost / danger / icon / text variants; small is
 * 36px (44px under 820px) with 12px type; icon button is a 44px square pill; disabled is 45 percent opacity.
 * The visual reference is stars.css §4 Buttons. Press scale(0.98) is not in the theme
 * (Tailwind has scale-95, not 98), so it is left out rather than approximated.
 */
import React, { forwardRef } from 'react';
import { cva } from 'class-variance-authority';
import { cn } from './cn';

const button = cva(
  [
    'tw-inline-flex tw-items-center tw-justify-center tw-gap-2 tw-rounded-pill tw-border tw-border-solid tw-border-transparent',
    'tw-font-bold tw-whitespace-nowrap tw-no-underline tw-cursor-pointer',
    'tw-transition-colors tw-duration-fast tw-ease-out',
    'focus-visible:tw-outline focus-visible:tw-outline-2 focus-visible:tw-outline-offset-2 focus-visible:tw-outline-focus-ring',
    'disabled:tw-opacity-45 disabled:tw-cursor-not-allowed aria-disabled:tw-opacity-45 aria-disabled:tw-cursor-not-allowed',
  ],
  {
    variants: {
      variant: {
        primary: 'tw-bg-action tw-text-action-text hover:tw-bg-action-hover',
        // DESIGN.md: a quiet button renders as ghost on `.stars-dark`.
        quiet: 'tw-bg-transparent tw-text-action-quiet-text tw-border-action-quiet-border hover:tw-bg-surface-sunken dark:tw-bg-ghost-bg dark:tw-border-transparent dark:tw-text-text dark:hover:tw-bg-ghost-bg-hover',
        ghost: 'tw-bg-ghost-bg tw-text-text hover:tw-bg-ghost-bg-hover',
        danger: 'tw-bg-transparent tw-text-danger tw-border-danger hover:tw-bg-surface-sunken',
        icon: 'tw-bg-transparent tw-text-text tw-border tw-border-divider hover:tw-bg-surface-sunken',
        text: 'tw-bg-transparent tw-text-link hover:tw-text-link-hover tw-underline tw-underline-offset-4',
      },
      size: {
        md: 'tw-min-h-11 tw-px-6 tw-text-sm',
        sm: 'tw-min-h-9 max-rail:tw-min-h-11 tw-px-4 tw-text-xs',
      },
      iconOnly: { true: 'tw-p-0', false: '' },
    },
    compoundVariants: [
      { iconOnly: true, size: 'md', class: 'tw-w-11 tw-h-11' },
      { iconOnly: true, size: 'sm', class: 'tw-w-9 tw-h-9 max-rail:tw-w-11 max-rail:tw-h-11' },
      // Text button: no box, but a 12px invisible hit margin above and below.
      { variant: 'text', class: 'tw-min-h-0 tw-px-0 tw-py-3 -tw-my-3 tw-border-0 tw-rounded-none tw-text-sm' },
    ],
    defaultVariants: { variant: 'primary', size: 'md', iconOnly: false },
  },
);

export const Button = forwardRef(function Button(
  { variant, size, iconOnly, loading = false, disabled, as: Component = 'button', className, onClick, children, ...props },
  ref,
) {
  const isButton = Component === 'button';
  const blocked = Boolean(disabled) || loading;
  return (
    <Component
      ref={ref}
      type={isButton ? props.type || 'button' : undefined}
      className={cn(button({ variant, size, iconOnly }), loading && 'tw-cursor-progress', className)}
      // Native `disabled` for a real disabled button; loading keeps focus, so it uses aria-disabled.
      disabled={isButton && disabled ? true : undefined}
      aria-disabled={blocked ? 'true' : undefined}
      aria-busy={loading ? 'true' : undefined}
      onClick={blocked ? (e) => e.preventDefault() : onClick}
      {...props}
    >
      {children}
    </Component>
  );
});

export { button as buttonVariants };
